// API client for backend communication
import axios, { AxiosInstance } from 'axios';
import { config } from './config';
import { getToken } from './auth';
import { Application, ContentResponse, User } from './types';

/**
 * sessionStorage key + TTL for the application-list IDENTITY cache consumed by
 * getApplicationsCached(). Versioned so a shape change invalidates old entries.
 */
const APPS_IDENTITY_CACHE_KEY = 'etas_apps_identity_v1';
const APPS_IDENTITY_CACHE_TTL_MS = 60_000;

interface AppsIdentityCacheEntry {
  savedAt: number;
  apps: Application[];
}

function readAppsIdentityCache(): Application[] | null {
  try {
    const raw = sessionStorage.getItem(APPS_IDENTITY_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const entry = JSON.parse(raw) as AppsIdentityCacheEntry;
    if (!entry || !Array.isArray(entry.apps) || typeof entry.savedAt !== 'number') {
      return null;
    }
    if (Date.now() - entry.savedAt > APPS_IDENTITY_CACHE_TTL_MS) {
      return null;
    }
    return entry.apps;
  } catch (e) {
    return null;
  }
}

function writeAppsIdentityCache(apps: Application[]): void {
  try {
    const entry: AppsIdentityCacheEntry = { savedAt: Date.now(), apps };
    sessionStorage.setItem(APPS_IDENTITY_CACHE_KEY, JSON.stringify(entry));
  } catch (e) {
    // Private mode / quota exceeded — caching is a pure optimisation, never fatal.
  }
}

class ApiClient {
  private client: AxiosInstance;

  /**
   * In-flight de-duplication for getApplicationsCached(). React 18 StrictMode
   * (dev) mounts DocumentViewer twice, so without this the identity lookup fires
   * two concurrent /apps requests on every single page view.
   */
  private appsIdentityInFlight: Promise<Application[]> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable sending/receiving cookies
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle 401 responses
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Don't log 401 for expected auth checks when user is not logged in
          const url = error.config?.url || '';
          const isDocRequest = url.includes('/docs/');
          const isAuthMeRequest = url.includes('/auth/me');
          if (!isDocRequest && !isAuthMeRequest) {
            console.error('Authentication error:', error.response.data);
          }
          // DocumentViewer component handles 401s by showing auth dialog or auto-redirecting
        }
        return Promise.reject(error);
      }
    );
  }

  async getApplications(): Promise<Application[]> {
    const response = await this.client.get<Application[]>('/apps');
    return response.data;
  }

  /**
   * getApplications() for callers that need the list purely as an IDENTITY source
   * (slug -> appId), de-duplicated in-flight and cached in sessionStorage for a short
   * TTL.
   *
   * Why this exists: DocumentViewer replaces the whole top-level document via
   * document.write(), so every in-document TOC click is a FULL page load that re-boots
   * the SPA from scratch. The bare-slug URL scheme carries no appId, so each of those
   * loads re-fetched the entire application list — an extra upstream round trip on every
   * navigation (twice in dev under StrictMode) just to map a slug to a uuid that had not
   * changed. An in-memory cache cannot help across a full document replacement, hence
   * sessionStorage (per-tab, same-origin, cleared when the tab closes).
   *
   * Only safe for identity. The list also carries `requiresAuth`, which flips when the
   * user signs in — never use a cached entry to decide access. Catalog.tsx deliberately
   * keeps calling getApplications() so the rendered list is always live.
   *
   * A slug missing from a cached list is NOT proof the application does not exist (it
   * may have been published within the TTL); callers must invalidate and retry against a
   * fresh list before reporting 404 — see DocumentViewer.loadDocument().
   */
  async getApplicationsCached(): Promise<Application[]> {
    const cached = readAppsIdentityCache();
    if (cached) {
      return cached;
    }
    if (!this.appsIdentityInFlight) {
      this.appsIdentityInFlight = this.getApplications()
        .then((apps) => {
          writeAppsIdentityCache(apps);
          return apps;
        })
        .finally(() => {
          this.appsIdentityInFlight = null;
        });
    }
    return this.appsIdentityInFlight;
  }

  /** Drops the getApplicationsCached() entry. Call after sign-in/out, or before a retry. */
  invalidateApplicationsCache(): void {
    try {
      sessionStorage.removeItem(APPS_IDENTITY_CACHE_KEY);
    } catch (e) {
      // ignore
    }
  }

  /** True when getApplicationsCached() would currently answer from cache. */
  hasCachedApplications(): boolean {
    return readAppsIdentityCache() !== null;
  }

  async getContent(appId: string, version?: string): Promise<ContentResponse> {
    const url = version
      ? `/content/${appId}/${version}`
      : `/content/${appId}`;
    const response = await this.client.get<ContentResponse>(url);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<{ user: User }>('/auth/me');
    return response.data.user;
  }

  /**
   * Fetches a topic page through the backend proxy.
   *
   * Do NOT add a parameter here to select the in-document nav link scheme. The backend
   * infers it from `version` on purpose: the browser re-requests this endpoint for every
   * relative sub-resource of the returned page (via the injected `<base href>`), and a
   * query param would not survive that resolution while `version` — being a path segment
   * of `<base href>` — does.
   */
  async getDocumentationHtml(appId: string, appNameSlug: string, version: string, resource: string = 'index.html'): Promise<string> {
    const response = await this.client.get<string>(`/docs/${appId}/${appNameSlug}/${version}/${resource}`, {
      responseType: 'text',
    });
    return response.data;
  }

  /**
   * Builds the backend URL for a binary/non-HTML documentation resource under a stable,
   * shareable nav path, for navigating the browser directly to it (e.g. via
   * window.location.replace) so the backend serves the bytes natively instead of a lossy
   * responseType:'text' fetch + document.write().
   *
   * `baseNavPath` comes from the caller's resolved DocumentTarget and is therefore
   * already in whichever scheme the user is on — `/{slug}` or
   * `/application/{appId}/{slug}/{version}`. This function deliberately does NOT choose a
   * scheme: emitting the old shape for a bare-slug page would send every PDF/download
   * through a pointless extra 302 in the compatibility shim, and emitting the bare-slug
   * shape for a preview page would silently drop the pinned revision and serve `main`.
   *
   * Always targets the BACKEND origin: in dev the Vite server proxies only `/api`, so a
   * same-origin binary URL would 404 on Vite instead of reaching the backend.
   */
  getApplicationResourceUrl(baseNavPath: string, resource: string): string {
    const isDev = import.meta.env?.DEV ?? false;
    const backendOrigin = isDev ? 'http://localhost:3000' : window.location.origin;
    return `${backendOrigin}${baseNavPath}/${resource}`;
  }

  async checkContentAccess(appId: string): Promise<{ ok: boolean }> {
    // lightweight endpoint to validate session and IDP for an application
    const response = await this.client.get<{ ok: boolean }>(`/content/${appId}/check`);
    return response.data;
  }

  async completeOAuthCallback(code: string, codeVerifier: string, state: string): Promise<{ success: boolean; user: { id: string; email: string; name: string }; expiresAt: string }> {
    const response = await this.client.post('/auth/callback', {
      code,
      codeVerifier,
      state,
    }, {
      withCredentials: true, // Include cookies in request
    });
    // `requiresAuth` on every application just changed — drop the identity cache.
    this.invalidateApplicationsCache();
    return response.data;
  }

  async logout(): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean }>('/auth/logout', {}, {
      withCredentials: true,
    });
    this.invalidateApplicationsCache();
    return response.data;
  }

  async getUserImage(userId: string): Promise<string | null> {
    // Fetch user profile image as base64 JSON (matches inca365-ref pattern)
    try {
      const response = await this.client.get<{ userImage: string | null; userId: string }>(
        `/users/${userId}/profile-photo`,
        { withCredentials: true }
      );
      
      if (response.data.userImage) {
        // Convert base64 to data URL
        return `data:image/;base64,${response.data.userImage}`;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user image:', error);
      return null;
    }
  }
}

export const api = new ApiClient();
